import { useModalContext } from '@context/Modal';
import constate from 'constate';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {isSmartTv} from "@utils/isSmartTv";

const KEY_WEB_A = 65;
const KEY_TV_BACK = 10009;
const KEY_BACK_EVENT = isSmartTv ? KEY_TV_BACK : KEY_WEB_A;

const useBackHandler = () => {
  const navigate = useNavigate();
  const { visible, closeModal } = useModalContext();
  const [canGoBack, setCanGoBack] = useState(true);
  const [callback, setCallback] = useState<() => () => boolean>();

  const handleBackButton = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.keyCode === KEY_BACK_EVENT) {
        if (visible) {
          closeModal();
        } else {
          if (!canGoBack) return;

          if (callback) {
            if (!callback()) return;
          }

          navigate(-1);
        }
      }
    },
    [visible, closeModal, canGoBack, callback, navigate]
  );

  /**
   * 뒤로가기 동작에대한 하드웨어 Interrupt때문에
   * 비동기인 useEffect가 아닌 useLayoutEffect로 선언하는것을 권장합니다.
   * removeCallback을 return으로 제공합니다
   * callback의 return값을 false로 주면 해당로직을 실행하고 뒤로가는것을 막습니다.
   */
  const addCallback = useCallback((callback: () => boolean) => {
    setCallback(() => callback);
    return () => {
      setCallback(undefined);
    };
  }, []);

  /**
   * 뒤로가기 동작에대한 하드웨어 Interrupt때문에
   * 비동기인 useEffect가 아닌 useLayoutEffect로 선언하는것을 권장합니다.
   */
  const disableBack = useCallback(() => {
    setCanGoBack(false);

    return () => {
      setCanGoBack(true);
    };
  }, [setCanGoBack]);

  useEffect(() => {
    window.addEventListener('keydown', handleBackButton);
    return () => {
      window.removeEventListener('keydown', handleBackButton);
    };
  }, [handleBackButton]);

  return {
    disableBack,
    addCallback
  };
};

export const [BackHandlerProvider, useBackHandlerContext] = constate(useBackHandler);
